import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Screen Wake Lock hook. Keeps the device screen awake while the sentinel
 * is held. Re-acquires on visibilitychange because iOS/some browsers release
 * the lock when the tab is backgrounded.
 */
export default function useScreenWakeLock(enabled = true) {
  const [active, setActive] = useState(false);
  const [supported] = useState(
    typeof navigator !== "undefined" && "wakeLock" in navigator
  );
  const sentinelRef = useRef(null);

  const request = useCallback(async () => {
    if (!supported) return;
    try {
      sentinelRef.current = await navigator.wakeLock.request("screen");
      setActive(true);
      sentinelRef.current.addEventListener("release", () => {
        setActive(false);
      });
    } catch {
      setActive(false);
    }
  }, [supported]);

  const release = useCallback(async () => {
    if (sentinelRef.current) {
      try { await sentinelRef.current.release(); } catch {}
      sentinelRef.current = null;
      setActive(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled || !supported) return;
    request();
    const onVisibility = () => {
      if (!document.hidden) request();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      release();
    };
  }, [enabled, supported, request, release]);

  return { supported, active };
}
